/**
 * The `StandardQuestionCatalog` codes this screen addresses answers by.
 *
 * An answer is keyed by `SurveyQuestionView.Key`, and for a standard question that key IS the
 * catalog code. Two surfaces read the same codes — `api/clientDetailSource` for the Przegląd tile
 * and `model/surveyHistory` for the matrix's weight row — and they were two lists of the same
 * strings, which is one place for the pair to drift and one for a typo to go unnoticed.
 *
 * Strings only, so `api/` and `model/` can both read it without either importing the other.
 */

/** The INTAKE's weight question. Intake-only; asked once. */
export const INTAKE_WEIGHT_KEY = 'initial_weight';

/** The recurring check-in's weight question. Check-in-only, so no survey carries both. */
export const CHECKIN_WEIGHT_KEY = 'checkin_weight';

/** The composite full-body measurement. Carries the weight INSIDE one payload. */
export const COMPOSITE_MEASUREMENT_KEY = 'checkin_measurements';

/**
 * Every code that carries a plain weight reading.
 *
 * The same quantity under two codes because it is asked in two places: the backend writes the
 * intake's answer as the FIRST point of the body-measurement series (R14) and every check-in
 * APPENDS to that same series (R27). Weight is weight wherever it was collected.
 */
export const WEIGHT_KEYS: readonly string[] = [CHECKIN_WEIGHT_KEY, INTAKE_WEIGHT_KEY];

/** The intake's height question, in centimetres — the phone asks it beside the weight, unit and all. */
export const HEIGHT_KEY = 'height';

/**
 * The two codes the chart strip's INK is keyed to.
 *
 * Owner rule, 16 September 2026: blue is sleep, ember is energy, and every other series is neutral.
 * They are here rather than in `surveyHistory` because they are catalog identities, which is what
 * this file is for — and because the same two codes already drive the Przegląd tile's readings.
 */
export const SLEEP_CODE = 'checkin_sleep';
export const ENERGY_CODE = 'checkin_energy';

/**
 * Hours slept, as a number (`intake-survey` R37) — a separate question from `SLEEP_CODE`, which asks
 * how WELL the client slept on a scale. It is sleep all the same, so it takes sleep's ink; and its
 * answer is a quantity, so it is printed with its unit, as a weight is.
 */
export const SLEEP_HOURS_CODE = 'checkin_sleep_hours';

/**
 * The reading a question's answer IS, where it is one of the client's vitals — the wire's
 * `vitalType`, the same word `GET /vitals/catalog` names the reading by.
 */
export type QuestionVital = 'SleepDuration' | 'EnergyLevel';

/**
 * Which vital a question reports, or `null` for a question that reports none this build can name.
 *
 * The question's own `vitalType` decides wherever it carries one: that is the server saying what the
 * answer is, and it holds for a catalog question this build has never heard of. A snapshot frozen
 * before the field existed carries none, and only there is the catalog CODE read instead.
 */
export function questionVital(question: {
  readonly code: string | null;
  readonly vitalType?: string | null;
}): QuestionVital | null {
  if (question.vitalType != null) {
    return question.vitalType === 'SleepDuration' || question.vitalType === 'EnergyLevel' ? question.vitalType : null;
  }
  if (question.code === SLEEP_HOURS_CODE) return 'SleepDuration';
  return question.code === ENERGY_CODE ? 'EnergyLevel' : null;
}
